import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import UnverifiedEmailBanner from "./components/UnverifiedEmailBanner";
import { trackPageView } from "./lib/analytics";
import TextUsWidget from "./components/TextUsWidget";
import CallbackWidget from "./components/CallbackWidget";
import PainPointWidget from "./components/PainPointWidget";
import ScrollToTop from "./components/ScrollToTop";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

// GA4: dispatch a page_view on every SPA route change.
function RouteChangeTracker() {
    const { pathname, search } = useLocation();
    useEffect(() => {
        // Wait a tick so document.title has settled after the new route renders
        const t = setTimeout(() => trackPageView(pathname + search, document.title), 50);
        return () => clearTimeout(t);
    }, [pathname, search]);
    return null;
}

// Web-Techservices partner attribution: any time a route loads with ?ref=CODE,
// stash it in a 30-day cookie. On signup the cookie is read and posted to the
// auth/register endpoint so the partner gets credited $100 by WTS.
function WtsAffiliateCapture() {
    const { search } = useLocation();
    useEffect(() => {
        import("./lib/wtsPartner").then(m => m.captureRefFromUrl());
    }, [search]);
    return null;
}

// Mount the two floating widgets only on public marketing/landing pages.
// Hidden on admin/super/dashboard surfaces and any /show/* big-screen modes.
function PublicWidgets() {
    const { pathname } = useLocation();
    const hideOn = ['/admin', '/super', '/dashboard', '/show', '/signage', '/scoreboard', '/kiosk', '/event-checkout', '/event/', '/site/', '/buy/', '/tag-auctioneer/'];
    // Volunteer staff-login surfaces are kiosk-like — keep them clean (but leave public /volunteer/:slug alone)
    const hideExact = ['/volunteer/pin', '/volunteer/login', '/volunteer/accept', '/volunteer/home', '/volunteer/admission', '/volunteer/pos', '/volunteer/quick-sale', '/volunteer/check-in', '/volunteer/recording', '/auctioneer'];
    if (hideExact.some((p) => pathname === p || pathname.startsWith(p + '/'))) return null;
    if (hideOn.some((p) => pathname === p || pathname.startsWith(p + '/'))) return null;
    return (
        <>
            <TextUsWidget source="CF" />
            <CallbackWidget source="CF" />
            <PainPointWidget />
        </>
    );
}

// Wrapper to feed URL :sectorSlug into SectorLandingPage
function SectorRouter() {
    const { sectorSlug } = useParams();
    return <SectorLandingPage slug={sectorSlug} />;
}

// Wrappers for programmatic SEO pages
function CompetitorRouter() {
    const { competitorSlug } = useParams();
    return <CompetitorPage slug={competitorSlug} />;
}
function CategoryRouter({ slug }) {
    return <CategoryLandingPage slug={slug} />;
}

// Landing page is kept eager for the fastest possible first paint on the most
// important public/conversion surface. Every other page is code-split with
// React.lazy so the initial bundle stays small (admin/super/event tooling is
// only fetched when an authenticated user actually navigates to it).
import LandingPage from "./pages/LandingPage";
// getCategorySlugs is used at module scope to build static routes, so this
// module is imported eagerly.
import CategoryLandingPage, { getCategorySlugs } from "./pages/CategoryLandingPage";

const LiveDemoPage = lazy(() => import("./pages/LiveDemoPage"));
const PrivacyPage = lazy(() => import("./pages/LegalPages").then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import("./pages/LegalPages").then((m) => ({ default: m.TermsPage })));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const OrgSignupPage = lazy(() => import("./pages/OrgSignupPage"));
const StorefrontPage = lazy(() => import("./pages/StorefrontPage"));
const ItemDetailPage = lazy(() => import("./pages/ItemDetailPage"));
const BidderDashboard = lazy(() => import("./pages/BidderDashboard"));
const PaymentResultPage = lazy(() => import("./pages/PaymentResultPage"));

const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const DemoSetupGuide = lazy(() => import("./pages/DemoSetupGuide"));
const PrintPaddles = lazy(() => import("./pages/PrintPaddles"));
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const AdminAuctions = lazy(() => import("./pages/admin/AdminAuctions"));
const AdminItems = lazy(() => import("./pages/admin/AdminItems"));
const AdminBidders = lazy(() => import("./pages/admin/AdminBidders"));
const AdminWinners = lazy(() => import("./pages/admin/AdminWinners"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminGivingPages = lazy(() => import("./pages/admin/AdminGivingPages"));
const AdminP2P = lazy(() => import("./pages/admin/AdminP2P"));
const AdminEvents = lazy(() => import("./pages/admin/AdminEvents"));
const AdminDonors = lazy(() => import("./pages/admin/AdminDonors"));
const AdminEmbed = lazy(() => import("./pages/admin/AdminEmbed"));
const AdminEmail = lazy(() => import("./pages/admin/AdminEmail"));
const AdminPayouts = lazy(() => import("./pages/admin/AdminPayouts"));
const AdminRaffles = lazy(() => import("./pages/admin/AdminRaffles"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminReportsTrends = lazy(() => import("./pages/admin/AdminReportsTrends"));
const AdminRenewals = lazy(() => import("./pages/admin/AdminRenewals"));
const AdminLTVLab = lazy(() => import("./pages/admin/AdminLTVLab"));
const AdminDonorIntel = lazy(() => import("./pages/admin/AdminDonorIntel"));
const AdminTasks = lazy(() => import("./pages/admin/AdminTasks"));
const AdminPortfolio = lazy(() => import("./pages/admin/AdminPortfolio"));
const AdminLeaderboard = lazy(() => import("./pages/admin/AdminLeaderboard"));
const AdminTaxStatements = lazy(() => import("./pages/admin/AdminTaxStatements"));
const AdminStripeSetup = lazy(() => import("./pages/admin/AdminStripeSetup"));
const AdminRefundsDisputes = lazy(() => import("./pages/admin/AdminRefundsDisputes"));
const AdminTemplates = lazy(() => import("./pages/admin/AdminTemplates"));
const AdminPromoCodes = lazy(() => import("./pages/admin/AdminPromoCodes"));
const AdminRecurring = lazy(() => import("./pages/admin/AdminRecurring"));
const AdminCRM = lazy(() => import("./pages/admin/AdminCRM"));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts"));
const AdminAutomations = lazy(() => import("./pages/admin/AdminAutomations"));
const AdminGolf = lazy(() => import("./pages/admin/AdminGolf"));
const AdminFundANeed = lazy(() => import("./pages/admin/AdminFundANeed"));
const AdminCheckin = lazy(() => import("./pages/admin/AdminCheckin"));
const AdminGames = lazy(() => import("./pages/admin/AdminGames"));
const AdminFulfillment = lazy(() => import("./pages/admin/AdminFulfillment"));
const AdminSMS = lazy(() => import("./pages/admin/AdminSMS"));
const AdminAI = lazy(() => import("./pages/admin/AdminAI"));
const AdminAIMoat = lazy(() => import("./pages/admin/AdminAIMoat"));
const AdminTextToGive = lazy(() => import("./pages/admin/AdminTextToGive"));
const AdminWebhooks = lazy(() => import("./pages/admin/AdminWebhooks"));
const AdminQuickBooks = lazy(() => import("./pages/admin/AdminQuickBooks"));
const AdminGoogleCalendar = lazy(() => import("./pages/admin/AdminGoogleCalendar"));
const AdminTaxReports = lazy(() => import("./pages/admin/AdminTaxReports"));
const AdminDomains = lazy(() => import("./pages/admin/AdminDomains"));
const AdminVolunteers = lazy(() => import("./pages/admin/AdminVolunteers"));
const AdminTransparency = lazy(() => import("./pages/admin/AdminTransparency"));
const AdminMatchingGifts = lazy(() => import("./pages/admin/AdminMatchingGifts"));
const AdminProcurement = lazy(() => import("./pages/admin/AdminProcurement"));
const AdminInbox = lazy(() => import("./pages/admin/AdminInbox"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminDirectMail = lazy(() => import("./pages/admin/AdminDirectMail"));
const AdminReengagement = lazy(() => import("./pages/admin/AdminReengagement"));
const EventWizard = lazy(() => import("./pages/admin/EventWizard"));
const EventHub = lazy(() => import("./pages/admin/EventHub"));
const EventLiveCharges = lazy(() => import("./pages/admin/EventLiveCharges"));
const FundANeedCapturePage = lazy(() => import("./pages/admin/FundANeedCapturePage"));
const EventSettlement = lazy(() => import("./pages/admin/EventSettlement"));
const EventReport = lazy(() => import("./pages/admin/EventReport"));
const EventPOS = lazy(() => import("./pages/admin/EventPOS"));
const EventPOSItems = lazy(() => import("./pages/admin/EventPOSItems"));
const EventLabels = lazy(() => import("./pages/admin/EventLabels"));
const EventLabelReprint = lazy(() => import("./pages/admin/EventLabelReprint"));
const EventAdmission = lazy(() => import("./pages/admin/EventAdmission"));
const EventPlaybook = lazy(() => import("./pages/admin/EventPlaybook"));
const EventProgramEditor = lazy(() => import("./pages/admin/EventProgramEditor"));
const EventPrintCenter = lazy(() => import("./pages/admin/EventPrintCenter"));
const EventShifts = lazy(() => import("./pages/admin/EventShifts"));
const EventDebrief = lazy(() => import("./pages/admin/EventDebrief"));
const EventCheckoutPage = lazy(() => import("./pages/EventCheckoutPage"));
const VolunteerPublic = lazy(() => import("./pages/VolunteerPublic"));
const VolunteerPinLogin = lazy(() => import("./pages/volunteer/VolunteerPinLogin"));
const VolunteerEmailLogin = lazy(() => import("./pages/volunteer/VolunteerEmailLogin"));
const VolunteerCheckIn = lazy(() => import("./pages/volunteer/VolunteerCheckIn"));
const FindAuctioneer = lazy(() => import("./pages/FindAuctioneer"));
const AuctioneerStatePage = lazy(() => import("./pages/AuctioneerStatePage"));
const AuctioneerCityPage = lazy(() => import("./pages/AuctioneerCityPage"));
const AuctioneerProfilePage = lazy(() => import("./pages/AuctioneerProfilePage"));
const AuctioneerJoinPage = lazy(() => import("./pages/AuctioneerJoinPage"));
const SuperAuctioneers = lazy(() => import("./pages/super/SuperAuctioneers"));
const SuperAuctioneerOutreach = lazy(() => import("./pages/super/SuperAuctioneerOutreach"));
const SuperUrlHealth = lazy(() => import("./pages/super/SuperUrlHealth"));
const SuperFeatured = lazy(() => import("./pages/super/SuperFeatured"));
const SuperPitchSheet = lazy(() => import("./pages/super/SuperPitchSheet"));
const SuperSmsLog = lazy(() => import("./pages/super/SuperSmsLog"));
const VolunteerAcceptInvite = lazy(() => import("./pages/volunteer/VolunteerAcceptInvite"));
const VolunteerHome = lazy(() => import("./pages/volunteer/VolunteerHome"));
const VolunteerAdmission = lazy(() => import("./pages/volunteer/VolunteerAdmission"));
const VolunteerPOS = lazy(() => import("./pages/volunteer/VolunteerPOS"));
const VolunteerQuickSale = lazy(() => import("./pages/volunteer/VolunteerQuickSale"));
const VolunteerRecording = lazy(() => import("./pages/volunteer/VolunteerRecording"));
const AuctioneerLogin = lazy(() => import("./pages/auctioneer/AuctioneerLogin"));
const AuctioneerStation = lazy(() => import("./pages/auctioneer/AuctioneerStation"));
const BuyItemPage = lazy(() => import("./pages/BuyItemPage"));
const TagAuctioneerPage = lazy(() => import("./pages/TagAuctioneerPage"));
const TransparencyPage = lazy(() => import("./pages/TransparencyPage"));
const TourPage = lazy(() => import("./pages/TourPage"));
const BlogIndex = lazy(() => import("./pages/BlogPages").then((m) => ({ default: m.BlogIndex })));
const BlogPostPage = lazy(() => import("./pages/BlogPages").then((m) => ({ default: m.BlogPostPage })));
const CompareCompetitorsPage = lazy(() => import("./pages/CompareCompetitorsPage"));
const SectorLandingPage = lazy(() => import("./pages/SectorLandingPage"));
const ProblemsWeSolvePage = lazy(() => import("./pages/ProblemsWeSolvePage"));
const CompetitorPage = lazy(() => import("./pages/CompetitorPage"));
const SilentAuctionBidCalculator = lazy(() => import("./pages/SilentAuctionBidCalculator"));
const PaddleRaiseScriptGenerator = lazy(() => import("./pages/PaddleRaiseScriptGenerator"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ShowPage = lazy(() => import("./pages/ShowPage"));
const EventShowDirector = lazy(() => import("./pages/admin/EventShowDirector"));
const WalkinRegister = lazy(() => import("./pages/WalkinRegister"));
const WalkinPaidPage = lazy(() => import("./pages/WalkinPaidPage"));
const GuestClaimPage = lazy(() => import("./pages/GuestClaimPage"));

const GivingPage = lazy(() => import("./pages/GivingPage"));
const P2PCampaignPage = lazy(() => import("./pages/P2PCampaignPage"));
const P2PParticipantPage = lazy(() => import("./pages/P2PParticipantPage"));
const EventPage = lazy(() => import("./pages/EventPage"));
const ScoreboardPage = lazy(() => import("./pages/ScoreboardPage"));
const OrgPublicProfilePage = lazy(() => import("./pages/OrgPublicProfilePage"));
const RafflePage = lazy(() => import("./pages/RafflePage"));
const OrgMicrositePage = lazy(() => import("./pages/OrgMicrositePage"));
const KioskPage = lazy(() => import("./pages/KioskPage"));
const FundANeedPage = lazy(() => import("./pages/FundANeedPage"));
const SignagePage = lazy(() => import("./pages/SignagePage"));

const SuperAdminLayout = lazy(() => import("./pages/SuperAdminLayout"));
const SuperOverview = lazy(() => import("./pages/super/SuperOverview"));
const SuperOrgs = lazy(() => import("./pages/super/SuperOrgs"));
const SuperTransactions = lazy(() => import("./pages/super/SuperTransactions"));
const SuperPayouts = lazy(() => import("./pages/super/SuperPayouts"));
const SuperReports = lazy(() => import("./pages/super/SuperReports"));
const SuperSettings = lazy(() => import("./pages/super/SuperSettings"));
const SuperProcurement = lazy(() => import("./pages/super/SuperProcurement"));
const SuperEvents = lazy(() => import("./pages/super/SuperEvents"));
const SuperOrgDetail = lazy(() => import("./pages/super/SuperOrgDetail"));
const SuperDemoBuilder = lazy(() => import("./pages/super/SuperDemoBuilder"));
const SuperSetupRequests = lazy(() => import("./pages/super/SuperSetupRequests"));
const SuperFeedback = lazy(() => import("./pages/super/SuperFeedback"));
const DemoDonatePage = lazy(() => import("./pages/DemoDonatePage"));
const SeatingChartKiosk = lazy(() => import("./pages/SeatingChartKiosk"));
const TableChatPage = lazy(() => import("./pages/TableChatPage"));
const EventTableChat = lazy(() => import("./pages/admin/EventTableChat"));
const TableChatQRCards = lazy(() => import("./pages/admin/TableChatQRCards"));
const SuggestionBox = lazy(() => import("./pages/admin/SuggestionBox"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const MyTicketPage = lazy(() => import("./pages/MyTicketPage"));
const MyWatchlistPage = lazy(() => import("./pages/MyWatchlistPage"));
const AuctionLeaderboardPage = lazy(() => import("./pages/AuctionLeaderboardPage"));
const BigScreenShowPage = lazy(() => import("./pages/BigScreenShowPage"));
import { captureSignupSource } from "./lib/signupSource";

// First-touch signup attribution (competitor referral tracking)
captureSignupSource();

// Minimal full-screen fallback shown while a lazy-loaded route chunk is fetched.
function PageLoader() {
    return (
        <div
            data-testid="route-loading"
            style={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#ffffff",
            }}
        >
            <div
                aria-label="Loading"
                style={{
                    width: 36,
                    height: 36,
                    border: "3px solid #e5e7eb",
                    borderTopColor: "#4f46e5",
                    borderRadius: "50%",
                    animation: "cf-spin 0.7s linear infinite",
                }}
            />
            <style>{`@keyframes cf-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

function IdlePrefetch() {
    // Warm the heaviest donor-facing route chunks during browser idle time so
    // public donation/event pages open instantly without bloating initial load.
    useEffect(() => {
        const prefetch = () => {
            import("./pages/GivingPage");
            import("./pages/EventPage");
            import("./pages/StorefrontPage");
        };
        const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
        const id = ric(prefetch, { timeout: 3000 });
        return () => {
            if (window.cancelIdleCallback) window.cancelIdleCallback(id);
        };
    }, []);
    return null;
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <ScrollToTop />
                <RouteChangeTracker />
                <WtsAffiliateCapture />
                <UnverifiedEmailBanner />
                <IdlePrefetch />
                <Suspense fallback={<PageLoader />}>
                <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/demo" element={<LiveDemoPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/tour" element={<TourPage />} />
                    <Route path="/compare" element={<CompareCompetitorsPage />} />
                    <Route path="/problems-we-solve" element={<ProblemsWeSolvePage />} />
                    <Route path="/blog" element={<BlogIndex />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/find-an-auctioneer" element={<FindAuctioneer />} />
                    <Route path="/find-an-auctioneer/join" element={<AuctioneerJoinPage />} />
                    <Route path="/find-an-auctioneer/:state" element={<AuctioneerStatePage />} />
                    <Route path="/find-an-auctioneer/:state/:citySlug" element={<AuctioneerCityPage />} />
                    <Route path="/auctioneer/:slug" element={<AuctioneerProfilePage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/for/:sectorSlug" element={<SectorRouter />} />
                    <Route path="/vs/:competitorSlug" element={<CompetitorRouter />} />
                    {getCategorySlugs().map((s) => (
                        <Route key={s} path={`/${s}`} element={<CategoryRouter slug={s} />} />
                    ))}
                    <Route path="/tools/silent-auction-bid-calculator" element={<SilentAuctionBidCalculator />} />
                    <Route path="/tools/paddle-raise-script-generator" element={<PaddleRaiseScriptGenerator />} />                    <Route path="/show/:slug" element={<ShowPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/print-paddles" element={<PrintPaddles />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/signup-org" element={<OrgSignupPage />} />
                    <Route path="/dashboard" element={<BidderDashboard />} />

                    <Route path="/a/:slug" element={<StorefrontPage />} />
                    <Route path="/a/:slug/item/:itemId" element={<ItemDetailPage />} />

                    {/* Phase 2 public routes */}
                    <Route path="/give/:slug" element={<GivingPage />} />
                    <Route path="/give/:slug/:pageSlug" element={<GivingPage />} />
                    <Route path="/p2p/:slug/:campaignSlug" element={<P2PCampaignPage />} />
                    <Route path="/p2p/:slug/:campaignSlug/:participantSlug" element={<P2PParticipantPage />} />
                    <Route path="/events/:slug/:eventSlug" element={<EventPage />} />
                    <Route path="/raffle/:slug/:raffleSlug" element={<RafflePage />} />
                    <Route path="/o/:slug" element={<OrgMicrositePage />} />
                    <Route path="/site/:slug" element={<OrgPublicProfilePage />} />
                    <Route path="/scoreboard/:slug" element={<ScoreboardPage />} />
                    <Route path="/kiosk/:slug" element={<KioskPage />} />
                    <Route path="/signage/:slug" element={<SignagePage />} />
                    <Route path="/volunteer/pin" element={<VolunteerPinLogin />} />
                    <Route path="/volunteer/login" element={<VolunteerEmailLogin />} />
                    <Route path="/volunteer/accept" element={<VolunteerAcceptInvite />} />
                    <Route path="/volunteer/home" element={<VolunteerHome />} />
                    <Route path="/volunteer/admission" element={<VolunteerAdmission />} />
                    <Route path="/volunteer/check-in" element={<VolunteerCheckIn />} />
                    <Route path="/volunteer/pos" element={<VolunteerPOS />} />
                    <Route path="/volunteer/quick-sale" element={<VolunteerQuickSale />} />
                    <Route path="/volunteer/recording" element={<VolunteerRecording />} />
                    <Route path="/auctioneer" element={<AuctioneerLogin />} />
                    <Route path="/auctioneer/station" element={<AuctioneerStation />} />
                    <Route path="/buy/:eventId/:itemCode" element={<BuyItemPage />} />
                    <Route path="/tag-auctioneer/:token" element={<TagAuctioneerPage />} />
                    <Route path="/volunteer/:slug" element={<VolunteerPublic />} />
                    <Route path="/transparency/:slug" element={<TransparencyPage />} />
                    <Route path="/event/:id/register-at-door" element={<WalkinRegister />} />
                    <Route path="/claim/:token" element={<GuestClaimPage />} />
                    <Route path="/event/:id/walkin-paid" element={<WalkinPaidPage />} />
                    <Route path="/event-checkout/:token" element={<EventCheckoutPage />} />
                    <Route path="/fund-a-need/:fanId" element={<FundANeedPage />} />

                    <Route path="/payment/success" element={<PaymentResultPage result="success" />} />
                    <Route path="/payment/cancel" element={<PaymentResultPage result="cancel" />} />

                    {/* Sales-demo donation page — mock, no real processing */}
                    <Route path="/demo/donate/art-station" element={<DemoDonatePage />} />
                    <Route path="/demo/donate" element={<DemoDonatePage />} />

                    {/* Lobby kiosk: seating chart with name search (public) */}
                    <Route path="/event/:eventId/seating-chart" element={<SeatingChartKiosk />} />
                    <Route path="/event/:eventId/table/:tableId/chat" element={<TableChatPage />} />
                    <Route path="/event/:eventId/ticket/:paddle" element={<MyTicketPage />} />
                    <Route path="/me/watchlist" element={<MyWatchlistPage />} />
                    <Route path="/scoreboard/auction/:auctionId" element={<AuctionLeaderboardPage />} />
                    <Route path="/bigscreen/:slug" element={<BigScreenShowPage />} />

                    {/* Canonical features showcase */}
                    <Route path="/features" element={<FeaturesPage />} />

                    <Route path="/admin" element={<AdminLayout />}>
                        <Route index element={<AdminOverview />} />
                        <Route path="auctions" element={<AdminAuctions />} />
                        <Route path="auctions/:auctionId/items" element={<AdminItems />} />
                        <Route path="bidders" element={<AdminBidders />} />
                        <Route path="winners" element={<AdminWinners />} />
                        <Route path="giving" element={<AdminGivingPages />} />
                        <Route path="p2p" element={<AdminP2P />} />
                        <Route path="events" element={<AdminEvents />} />
                        <Route path="raffles" element={<AdminRaffles />} />
                        <Route path="donors" element={<AdminDonors />} />
                        <Route path="embed" element={<AdminEmbed />} />
                        <Route path="email" element={<AdminEmail />} />
                        <Route path="payouts" element={<AdminPayouts />} />
                        <Route path="reports" element={<AdminReports />} />
                        <Route path="reports/trends" element={<AdminReportsTrends />} />
                        <Route path="renewals" element={<AdminRenewals />} />
                        <Route path="ltv-lab" element={<AdminLTVLab />} />
                        <Route path="donor-intel" element={<AdminDonorIntel />} />
                        <Route path="tasks" element={<AdminTasks />} />
                        <Route path="portfolio" element={<AdminPortfolio />} />
                        <Route path="leaderboard" element={<AdminLeaderboard />} />
                        <Route path="tax-statements" element={<AdminTaxStatements />} />
                        <Route path="stripe-setup" element={<AdminStripeSetup />} />
                        <Route path="refunds-disputes" element={<AdminRefundsDisputes />} />
                        <Route path="templates" element={<AdminTemplates />} />
                        <Route path="promo-codes" element={<AdminPromoCodes />} />
                        <Route path="recurring" element={<AdminRecurring />} />
                        <Route path="crm" element={<AdminCRM />} />
                        <Route path="contacts" element={<AdminContacts />} />
                        <Route path="automations" element={<AdminAutomations />} />
                        <Route path="golf" element={<AdminGolf />} />
                        <Route path="fund-a-need" element={<AdminFundANeed />} />
                        <Route path="checkin" element={<AdminCheckin />} />
                        <Route path="demo-guide" element={<DemoSetupGuide />} />
                        <Route path="games" element={<AdminGames />} />
                        <Route path="fulfillment" element={<AdminFulfillment />} />
                        <Route path="sms" element={<AdminSMS />} />
                        <Route path="directmail" element={<AdminDirectMail />} />
                        <Route path="reengagement" element={<AdminReengagement />} />
                        <Route path="ai" element={<AdminAI />} />
                        <Route path="ai-moat" element={<AdminAIMoat />} />
                        <Route path="text-to-give" element={<AdminTextToGive />} />
                        <Route path="events/:id/show" element={<EventShowDirector />} />
                        <Route path="webhooks" element={<AdminWebhooks />} />
                        <Route path="quickbooks" element={<AdminQuickBooks />} />
                        <Route path="google-calendar" element={<AdminGoogleCalendar />} />
                        <Route path="tax-reports" element={<AdminTaxReports />} />
                        <Route path="custom-domain" element={<AdminDomains />} />
                        <Route path="volunteers" element={<AdminVolunteers />} />
                        <Route path="transparency" element={<AdminTransparency />} />
                        <Route path="matching-gifts" element={<AdminMatchingGifts />} />
                        <Route path="procurement" element={<AdminProcurement />} />
                        <Route path="events/new" element={<EventWizard />} />
                        <Route path="events/:id/hub" element={<EventHub />} />
                        <Route path="events/:id/live" element={<EventLiveCharges />} />
                        <Route path="events/:eventId/fund-a-need-capture" element={<FundANeedCapturePage />} />
                        <Route path="events/:id/settlement" element={<EventSettlement />} />
                        <Route path="events/:id/report" element={<EventReport />} />
                        <Route path="events/:id/pos" element={<EventPOS />} />
                        <Route path="events/:id/pos-items" element={<EventPOSItems />} />
                        <Route path="events/:id/labels" element={<EventLabels />} />
                        <Route path="events/:id/labels/reprint" element={<EventLabelReprint />} />
                        <Route path="events/:id/admission" element={<EventAdmission />} />
                        <Route path="events/:id/playbook" element={<EventPlaybook />} />
                        <Route path="events/:id/program" element={<EventProgramEditor />} />
                        <Route path="events/:id/print" element={<EventPrintCenter />} />
                        <Route path="events/:id/shifts" element={<EventShifts />} />
                        <Route path="events/:id/table-chat" element={<EventTableChat />} />
                        <Route path="events/:id/table-qr-cards" element={<TableChatQRCards />} />
                        <Route path="events/:id/debrief" element={<EventDebrief />} />
                        <Route path="settings" element={<AdminSettings />} />
                        <Route path="suggestion-box" element={<SuggestionBox />} />
                    </Route>

                    <Route path="/super" element={<SuperAdminLayout />}>
                        <Route index element={<SuperOverview />} />
                        <Route path="orgs" element={<SuperOrgs />} />
                        <Route path="transactions" element={<SuperTransactions />} />
                        <Route path="reports" element={<SuperReports />} />
                        <Route path="payouts" element={<SuperPayouts />} />
                        <Route path="procurement" element={<SuperProcurement />} />
                        <Route path="auctioneers" element={<SuperAuctioneers />} />
                        <Route path="auctioneer-outreach" element={<SuperAuctioneerOutreach />} />
                        <Route path="url-health" element={<SuperUrlHealth />} />
                        <Route path="featured" element={<SuperFeatured />} />
                        <Route path="pitch-sheet" element={<SuperPitchSheet />} />
                        <Route path="sms-log" element={<SuperSmsLog />} />
                        <Route path="inbox" element={<AdminInbox />} />
                        <Route path="events" element={<SuperEvents />} />
                        <Route path="orgs/:orgId" element={<SuperOrgDetail />} />
                        <Route path="demo-builder" element={<SuperDemoBuilder />} />
                        <Route path="demo-guide" element={<DemoSetupGuide isSuper />} />
                        <Route path="setup-requests" element={<SuperSetupRequests />} />
                        <Route path="blog" element={<AdminBlog />} />
                        <Route path="settings" element={<SuperSettings />} />
                        <Route path="feedback" element={<SuperFeedback />} />
                    </Route>

                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
                </Suspense>
                <PublicWidgets />
                <PWAInstallPrompt />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
